import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { LinkButton } from "~/common/components/Link";
import { MenuBottomCornersRounded, useSimpleMenuState } from "~/common/components/Menu/Menu";
import { MENU_POS_RIGHT } from "~/common/components/Menu/MenuPositions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import type { RootState } from "~/app/store";
import { isProductPage } from "~/utils/url/urlUtils";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import { MICROSOFT_TEAMS } from "~/utils/url/staticResources";
import { useTranslation } from "react-i18next";
import { C_PRIMARY } from "~/common/components/icon/Colors";
import { resolveTranslationSync } from "~/app/i18n/Translation";
import { styled } from "~/common/mui";
import ShareIcon from "@mui/icons-material/Share";
import MailIcon from "@mui/icons-material/Mail";
import CheckIcon from "@mui/icons-material/Check";
import LinkIcon from "@mui/icons-material/Link";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  width: "216px",
  whiteSpace: "break-spaces",
  i: {
    color: theme.palette.primary.main,
  },
}));

const COPY_TO_CLIPBOARD = {
  icon: <LinkIcon sx={{ fontSize: 20 }} />,
  text: "share.copy",
};
const COPIED_TO_CLIPBOARD = {
  icon: <CheckIcon sx={{ fontSize: 20 }} />,
  text: "share.copied",
};

const extractCodeFromUrl = () => {
  const pathName = window.location.pathname;
  const parts = pathName.split("/");
  return parts[parts.length - 1];
};

const ShareButton = () => {
  const [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] = useSimpleMenuState();
  const [clipboardMessage, setClipboardMessage] = useState(COPY_TO_CLIPBOARD);
  const isPdp = isProductPage();
  const isTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const getProductBasicInfo = (state: RootState) => state?.products.basic[extractCodeFromUrl()];
  const basicProductData = useSelector(getProductBasicInfo);

  const subject = t("share.subject", { product: basicProductData?.designation });

  const message = `${t("share.intro", {})}
${basicProductData?.designation}
${basicProductData && resolveTranslationSync(basicProductData?.shortDescription)}
${window.location.href}`;

  const messageWithoutUrl = `${t("share.intro", {})}
${basicProductData?.designation}
${basicProductData && resolveTranslationSync(basicProductData?.shortDescription)}`;

  const shareToMail = () => {
    const URI = `mailto:?subject=${encodeURI(subject)}&body=${encodeURI(message)}`;
    window.location.href = URI;
  };

  const shareToMSTeams = () => {
    const URI = `MSTeams:/l/chat/0/0?users=&message=${encodeURI(message)}`;
    window.location.href = URI;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setClipboardMessage(COPIED_TO_CLIPBOARD);
  };

  const divRef = useRef(null);

  const onClick = () => setAnchorEl(divRef.current);

  if (!isPdp) {
    return;
  }

  const mobileShare = async () => {
    try {
      await navigator.share({
        title: subject,
        text: messageWithoutUrl,
        url: window.location.href,
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (isTablet) {
    return (
      <div ref={divRef}>
        <LinkButton style={{ gap: "8px" }} onClick={onClick}>
          <ShareIcon className={"icon-margin-l scha__header__language--icon"} sx={{ fontSize: 20 }} />
          <span>{t("page.wishlist.action.share")}</span>
        </LinkButton>
        <MenuBottomCornersRounded
          anchorEl={anchorEl}
          open={isOpen}
          {...MENU_POS_RIGHT}
          onClose={() => {
            handleClose();
            setClipboardMessage(COPY_TO_CLIPBOARD);
          }}
          MenuListProps={{ disablePadding: true }}
        >
          <StyledMenuItem onClick={shareToMail}>
            <MailIcon sx={{ color: C_PRIMARY, fontSize: 16 }} />
            {t("share.email")}
          </StyledMenuItem>
          <StyledMenuItem onClick={shareToMSTeams}>
            <img src={MICROSOFT_TEAMS} />
            {t("share.msteams")}
          </StyledMenuItem>
          <StyledMenuItem onClick={copyToClipboard}>
            {clipboardMessage.icon}
            {t(clipboardMessage.text)}
          </StyledMenuItem>
        </MenuBottomCornersRounded>
      </div>
    );
  }

  return (
    <LinkButton style={{ gap: "8px" }} onClick={mobileShare}>
      <ShareIcon className={"icon-margin-l scha__header__language--icon"} sx={{ fontSize: 20 }} />
    </LinkButton>
  );
};

export default ShareButton;
